import { FINANCE_CONFIG } from './config.js';
import { addDaysUTC, addMonthsUTC, createUTCDate, differenceInUTCDays, endOfUTCMonth, parseDateUTC, toDateKeyUTC, } from './dates.js';
import { getCurrentWalletBalance, toProjectionMovement } from './ledger.js';
import { occursOnDate } from './recurrence.js';
export function calculateProjection(transactions, options) {
    const today = parseDateUTC(options.today);
    const monthsAhead = options.monthsAhead ?? 6;
    if (!Number.isInteger(monthsAhead) || monthsAhead < 1) {
        throw new RangeError('monthsAhead must be a positive integer');
    }
    assertSupportedYear(today);
    const requestedHorizon = addMonthsUTC(today, monthsAhead);
    const maximumHorizon = createUTCDate(FINANCE_CONFIG.projection.maxYear, 12, 31);
    const horizon = requestedHorizon > maximumHorizon ? maximumHorizon : requestedHorizon;
    const monthEnd = endOfUTCMonth(today);
    const importantThreshold = options.importantThreshold ?? FINANCE_CONFIG.projection.importantThreshold;
    const points = [];
    let balance = 0;
    let endMonthBalance = 0;
    let nextImportant = null;
    let lowPoint = { balance: 0, date: toDateKeyUTC(today) };
    for (let date = today; date <= horizon; date = addDaysUTC(date, 1)) {
        const movements = transactions
            .filter((transaction) => occursOnDate(transaction, date, options))
            .map((transaction) => toProjectionMovement(transaction, date));
        const net = movements.reduce((sum, movement) => sum + movement.net, 0);
        balance += net;
        nextImportant ??=
            movements.find((movement) => Math.abs(movement.net) >= importantThreshold) ?? null;
        if (balance < lowPoint.balance) {
            lowPoint = { balance, date: toDateKeyUTC(date) };
        }
        if (date.getTime() === monthEnd.getTime()) {
            endMonthBalance = balance;
        }
        points.push({ balance, date: toDateKeyUTC(date), movements, net });
    }
    return {
        endMonthBalance,
        horizonBalance: balance,
        lowPoint,
        nextImportant,
        points,
        sixMonthBalance: balance,
        todayBalance: points[0].balance,
        truncated: requestedHorizon > maximumHorizon,
    };
}
export function getProjectionStatus(projection) {
    if (projection.lowPoint.balance < 0)
        return { label: 'Critical', status: 'critical' };
    if (projection.endMonthBalance < FINANCE_CONFIG.projection.riskBalance) {
        return { label: 'At risk', status: 'risk' };
    }
    return { label: 'Stable', status: 'stable' };
}
export function calculateMonthlyHealthTarget(transactions, options) {
    const today = parseDateUTC(options.today);
    const remainingDays = Math.max(1, differenceInUTCDays(endOfUTCMonth(today), today) + 1);
    const projection = calculateProjection(transactions, { ...options, monthsAhead: 1 });
    const currentBalance = options.includeWalletBalance === false ? 0 : getCurrentWalletBalance(options.wallets ?? []);
    const projectedEndBalance = currentBalance + projection.endMonthBalance;
    const deficit = Math.max(0, (options.targetFloor ?? 0) - projectedEndBalance);
    const requiredToday = Math.ceil((deficit / remainingDays) * 100) / 100;
    return {
        currentBalance,
        deficit,
        projectedEndBalance,
        remainingDays,
        requiredToday,
        status: deficit > 0 ? 'recover' : 'covered',
    };
}
function assertSupportedYear(date) {
    const year = date.getUTCFullYear();
    if (year < FINANCE_CONFIG.projection.minYear || year > FINANCE_CONFIG.projection.maxYear) {
        throw new RangeError('today is outside the supported projection range');
    }
}
//# sourceMappingURL=projection.js.map